/**
 * API service for Budget 2025 AutoRAG frontend
 * Handles all communication with Cloudflare Pages Functions and AutoRAG
 */

import axios from 'axios';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: '',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear invalid token
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Authentication API
export const authAPI = {
  verifyEmail: async (email) => {
    const response = await api.post('/api/auth/verify-email', { email });
    return response.data;
  },

  verifyToken: async () => {
    const response = await api.post('/api/auth/verify-token');
    return response.data;
  },

  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    return Promise.resolve({ success: true });
  },
};

// AutoRAG API
export const autoragAPI = {
  search: async (query) => {
    const response = await api.post('/api/autorag/search', { query });
    return response.data;
  },

  aiSearch: async (query) => {
    const response = await api.post('/api/autorag/ai-search', { query });
    return response.data;
  },
};

// Legacy chat API for compatibility
export const chatAPI = {
  sendMessage: async (message, conversationHistory = []) => {
    // Use AutoRAG AI search for conversational responses
    const response = await autoragAPI.aiSearch(message);
    
    // Transform AutoRAG response to match chat format
    return {
      response: response.answer || response.response || "I couldn't find information about that.",
      references: response.sources || [],
      total_results_found: response.sources?.length || 0,
      conversation_id: Date.now().toString()
    };
  },
};

// Utility functions
export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem('token', token);
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
  }
};

export const getAuthToken = () => {
  return localStorage.getItem('token');
};

export const isAuthenticated = () => {
  return !!getAuthToken();
};

export default api;